import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts'

interface TimeSeriesPoint {
  date: string
  visitors: number
  pageviews: number
}

interface AnnotationMarker {
  date: string
  label: string
  color?: string
}

interface AnalyticsAreaChartProps {
  data: TimeSeriesPoint[]
  previousData?: TimeSeriesPoint[]
  showComparison?: boolean
  annotations?: AnnotationMarker[]
  height?: number
  visitorLabel?: string
  pageviewLabel?: string
}

// Custom tooltip that shows delta when comparison data exists
function ComparisonTooltip({
  active,
  payload,
  label,
  visitorLabel,
  pageviewLabel
}: {
  active?: boolean
  payload?: Array<{ dataKey: string; value: number; payload: Record<string, unknown> }>
  label?: string
  visitorLabel: string
  pageviewLabel: string
}) {
  if (!active || !payload || payload.length === 0) return null

  const dataPoint = payload[0]?.payload || {}
  const visitors = (dataPoint.visitors as number) ?? 0
  const pageviews = (dataPoint.pageviews as number) ?? 0
  const prevVisitors = dataPoint.prevVisitors as number | undefined
  const prevPageviews = dataPoint.prevPageviews as number | undefined

  function formatDelta(current: number, previous: number | undefined): string {
    if (previous == null || previous === 0) return ''
    const delta = ((current - previous) / previous) * 100
    const sign = delta > 0 ? '+' : ''
    return ` (prev: ${previous}, ${sign}${delta.toFixed(1)}%)`
  }

  return (
    <div style={{
      backgroundColor: '#0f172a',
      border: 'none',
      borderRadius: '8px',
      padding: '8px 12px',
      fontSize: '12px',
      color: '#fff'
    }}>
      <p style={{ color: '#94a3b8', marginBottom: 4 }}>{label}</p>
      <p style={{ padding: '2px 0' }}>
        <span style={{ color: '#FFD500' }}>{visitorLabel}: </span>
        {visitors}
        {prevVisitors != null && (
          <span style={{ color: '#94a3b8' }}>{formatDelta(visitors, prevVisitors)}</span>
        )}
      </p>
      <p style={{ padding: '2px 0' }}>
        <span style={{ color: '#10b981' }}>{pageviewLabel}: </span>
        {pageviews}
        {prevPageviews != null && (
          <span style={{ color: '#94a3b8' }}>{formatDelta(pageviews, prevPageviews)}</span>
        )}
      </p>
    </div>
  )
}

export function AnalyticsAreaChart({
  data,
  previousData,
  showComparison = false,
  annotations,
  height = 240,
  visitorLabel = 'Visitors',
  pageviewLabel = 'Pageviews'
}: AnalyticsAreaChartProps) {
  if (data.length === 0) return null

  const hasComparison = showComparison && previousData && previousData.length > 0

  // Build chart data — merge current and previous period by index position
  const chartData = data.map((d, i) => {
    const prev = hasComparison ? previousData[i] : null
    return {
      ...d,
      displayDate: formatChartDate(d.date),
      ...(prev ? {
        prevVisitors: prev.visitors,
        prevPageviews: prev.pageviews,
        prevDate: prev.date
      } : {})
    }
  })

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
          <defs>
            <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#FFD500" stopOpacity={0.02} />
              <stop offset="95%" stopColor="#FFD500" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="colorPageviews" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.01} />
              <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
          <XAxis
            dataKey="displayDate"
            tick={{ fontSize: 11, fill: '#94a3b8' }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#94a3b8' }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
          />
          {hasComparison ? (
            <Tooltip
              content={
                <ComparisonTooltip
                  visitorLabel={visitorLabel}
                  pageviewLabel={pageviewLabel}
                />
              }
            />
          ) : (
            <Tooltip
              contentStyle={{
                backgroundColor: '#0f172a',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '12px',
                color: '#fff'
              }}
              labelStyle={{ color: '#94a3b8', marginBottom: 4 }}
              itemStyle={{ color: '#fff', padding: '2px 0' }}
              formatter={(value: number, name: string) => {
                const labels: Record<string, string> = {
                  visitors: visitorLabel,
                  pageviews: pageviewLabel,
                }
                return [value, labels[name] || name]
              }}
            />
          )}

          {/* Previous period comparison (dashed, no fill) */}
          {hasComparison && (
            <>
              <Area
                type="monotone"
                dataKey="prevVisitors"
                stroke="#FFD500"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                fill="none"
                dot={false}
                activeDot={{ r: 3, stroke: '#FFD500', strokeWidth: 1, fill: '#e2e8f0' }}
                opacity={0.4}
              />
              <Area
                type="monotone"
                dataKey="prevPageviews"
                stroke="#10b981"
                strokeWidth={1}
                strokeDasharray="4 4"
                fill="none"
                dot={false}
                activeDot={{ r: 2, stroke: '#10b981', strokeWidth: 1, fill: '#e2e8f0' }}
                opacity={0.3}
              />
            </>
          )}

          {/* Current period */}
          <Area
            type="monotone"
            dataKey="visitors"
            stroke="#FFD500"
            strokeWidth={1}
            fill="url(#colorVisitors)"
            dot={{ r: 0.8, fill: '#FFD500', stroke: 'none' }}
            activeDot={{ r: 4, stroke: '#FFD500', strokeWidth: 2, fill: '#fff' }}
          />
          <Area
            type="monotone"
            dataKey="pageviews"
            stroke="#10b981"
            strokeWidth={0.8}
            fill="url(#colorPageviews)"
            dot={false}
            activeDot={{ r: 3, stroke: '#10b981', strokeWidth: 2, fill: '#fff' }}
          />

          {/* Annotation vertical lines */}
          {annotations?.map(ann => (
            <ReferenceLine
              key={ann.date + ann.label}
              x={formatChartDate(ann.date)}
              stroke={ann.color || '#f59e0b'}
              strokeDasharray="3 3"
              strokeWidth={1.5}
              label={{
                value: ann.label,
                position: 'top',
                fill: ann.color || '#f59e0b',
                fontSize: 10,
                fontWeight: 600
              }}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>

      {/* Comparison legend */}
      {hasComparison && (
        <div className="flex items-center justify-center gap-6 mt-2 text-[11px] text-framed-neutral-500">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-5 h-0.5 bg-framed-yellow rounded-full" />
            <span>Current period</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-5 h-0.5 bg-framed-yellow/40 rounded-full" style={{ backgroundImage: 'repeating-linear-gradient(90deg, #FFD500 0, #FFD500 3px, transparent 3px, transparent 6px)' }} />
            <span>Previous period</span>
          </div>
        </div>
      )}
    </div>
  )
}

function formatChartDate(dateStr: string): string {
  const parts = dateStr.split('-')
  if (parts.length !== 3) return dateStr
  return `${parts[1]}/${parts[2]}`
}
